import React, { useState, useEffect, useMemo } from "react";
import { hasPermission } from "../utils/permissionHelper";
import Access from "../components/access";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import InventoryNav from "../components/inventoryNav";
import IndentModal from "../components/storeReqmodal";
import VehicleModal from "../components/indentVehicle";
import Time from "../components/time";
import Swal from "sweetalert2";
import { useNavigate, Link } from "react-router-dom";

const AddSfIssues = () => {
  useAuthRedirect();
  const navigate = useNavigate();

  // ---- local/session bits
  const branch = localStorage.getItem("branch");
  const userId = localStorage.getItem("username");

  // ---- UI state
  const [showModal, setShowModal] = useState(false);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [currentVehicleIndex, setCurrentVehicleIndex] = useState(null);

  // ---- data state
  const [stnNo, setStnNo] = useState("");
  const [dept, setDept] = useState([]);
  const [indent, setIndent] = useState([]); // single source of truth for table rows
  const [selectedIndent, setSelectedIndent] = useState(""); // indent header number chosen from modal

  // ---- top form state
  const [formData, setFormData] = useState({
    newPurNo: "",
    shift: "",
    dept: "",
    remarks: "",
  });

  // ---- permissions
  const rawPerms = useMemo(
    () => localStorage.getItem("permissions") || localStorage.getItem("userPermissions"),
    []
  );
  const permsLoaded = !!rawPerms; // eslint-disable-line no-unused-vars
  const canEntry = hasPermission("Stores Issue", "entry");
  const canVehicle = hasPermission("Hire Vehicle Registration", "entry");

  // ---- handlers
  const handleSelectIndent = (item) => {
    // item from modal is expected to include indentHeader_number
    setSelectedIndent(item?.indentHeader_number ?? "");
  };

  const fetchStnNo = async () => {
    try {
      Swal.fire({
        title: "Fetching Issue Number",
        html: `
          <div style="width: 100%; background: #e0e0e0; border-radius: 5px;">
            <div style="height: 10px; width: 0%; background: linear-gradient(to right, #4facfe, #00f2fe); animation: progressBar 2s linear infinite;"></div>
          </div>
          <p style="margin-top: 10px; font-size: 14px;">Please wait while we fetch Issue Number...</p>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          const style = document.createElement("style");
          style.innerHTML = `
            @keyframes progressBar {
              0% { width: 0% }
              50% { width: 100% }
              100% { width: 0% }
            }
          `;
          document.head.appendChild(style);
        },
      });

      const response = await axios.get(`https://rfs2.infraconsoft.net.in/api/sf_no?branch=${branch}`);
      if (response?.data?.sfNo) {
        const newSfNo = parseInt(response.data.sfNo, 10) || 0;
        setStnNo(String(newSfNo));
        setFormData((s) => ({ ...s, newPurNo: String(newSfNo) }));
      }
      Swal.close();
    } catch (error) {
      Swal.close();
      console.error("Error fetching Issue No:", error);
      Swal.fire({
        icon: "error",
        title: "Oops!",
        text: "Error fetching Issue No. Please try again",
      });
    }
  };

  // initial load: stn no & departments
  useEffect(() => {
    if (!branch || !canEntry) return;

    // fetch STN / SF number
    fetchStnNo();

    // fetch departments
    const fetchDept = async () => {
      try {
        Swal.fire({
          title: "Fetching Department",
          html: `
            <div style="width: 100%; background: #e0e0e0; border-radius: 5px;">
              <div style="height: 10px; width: 0%; background: linear-gradient(to right, #4facfe, #00f2fe); animation: progressBar 2s linear infinite;"></div>
            </div>
            <p style="margin-top: 10px; font-size: 14px;">Please wait while we fetch Departments...</p>
          `,
          showConfirmButton: false,
          allowOutsideClick: false,
          didOpen: () => {
            const style = document.createElement("style");
            style.innerHTML = `
              @keyframes progressBar {
                0% { width: 0% }
                50% { width: 100% }
                100% { width: 0% }
              }
            `;
            document.head.appendChild(style);
          },
        });

        const res = await axios.get(
          `https://rfs2.infraconsoft.net.in/api/storeReq_department?branch_code=${branch}`
        );
        setDept(res?.data?.dept || []);
        Swal.close();
      } catch (error) {
        Swal.close();
        console.error("Error fetching Departments:", error);
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: "Error fetching Departments. Please try again",
        });
      }
    };

    fetchDept();
  }, [branch, canEntry]);

  // when an indent is chosen, fetch its detail lines into `indent`
  useEffect(() => {
    const fetchIndentDetails = async () => {
      if (!branch || !selectedIndent) {
        setIndent([]);
        return;
      }
      try {
        const response = await axios.get(
          `https://rfs2.infraconsoft.net.in/api/purchaseReq_indentDetails?branch=${branch}&indentNo=${selectedIndent}`
        );
        const rows = response?.data?.indentDetails || [];
        // normalize rows with fields we will edit in UI
        const normalized = rows.map((r) => ({
          ...r,
          qty: r?.qty ?? "", // editable
          ref: r?.ref ?? "",
          vcode: r?.vcode ?? "",
          vnumber: r?.vnumber ?? "",
          _tempDeleted: false,
        }));
        setIndent(normalized);
      } catch (error) {
        console.error("Error fetching indent details:", error);
        setIndent([]);
      }
    };

    fetchIndentDetails();
  }, [branch, selectedIndent]);

  // generic top-form change handler (shift, dept, remarks, etc.)
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  // change a field in a row by index (qty, ref, etc.)
  const handleRowChange = (index, field, value) => {
    setIndent((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  // clear vehicle fields only (separate "delete" for vehicle)
  const handleClearVehicle = (index) => {
    setIndent((prev) =>
      prev.map((item, i) => (i === index ? { ...item, vcode: "", vnumber: "" } : item))
    );
  };

  // open vehicle modal for a particular row
  const openVehiclePicker = (index) => {
    setCurrentVehicleIndex(index);
    setShowVehicleModal(true);
  };

  // vehicle picked from modal -> write into SAME indent array
  const handleVehicleSelect = (vehicle_code, vehicle_number) => {
    if (!vehicle_code || !vehicle_number) {
      console.error("Vehicle selection error: Missing vehicle_code or vehicle_number");
      setShowVehicleModal(false);
      setCurrentVehicleIndex(null);
      return;
    }

    if (currentVehicleIndex !== null && currentVehicleIndex >= 0) {
      setIndent((prev) =>
        prev.map((item, i) =>
          i === currentVehicleIndex ? { ...item, vcode: vehicle_code, vnumber: vehicle_number } : item
        )
      );
    }

    setCurrentVehicleIndex(null);
    setShowVehicleModal(false);
  };

  // temp delete (remove row from current draft only)
  const handleDeleteRow = (index) => {
    setIndent((prev) => prev.filter((_, i) => i !== index));
  };

  // ---- submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // visible rows only (filter out temp removed, if any)
    const activeRows = indent.filter((r) => !r._tempDeleted);

    // must have at least one line
    if (activeRows.length === 0) {
      Swal.fire("Error", "Please add at least one item", "error");
      return;
    }

    // validate top form
    if (!formData.newPurNo?.trim()) {
      Swal.fire("Error", "Please enter SF No", "error");
      return;
    }
    if (!selectedIndent) {
      Swal.fire("Error", "Please select Indent No", "error");
      return;
    }
    if (!formData.shift?.trim()) {
      Swal.fire("Error", "Please select Shift", "error");
      return;
    }
    if (!formData.dept?.trim()) {
      Swal.fire("Error", "Please select Department", "error");
      return;
    }

    // validate rows
    for (let i = 0; i < activeRows.length; i++) {
      const item = activeRows[i];
      const qtyNum = Number(item.qty || 0);

      if (!qtyNum || qtyNum <= 0) {
        Swal.fire(
          "Error",
          `Enter a valid issued quantity for item: ${item.itemmaster_name}`,
          "error"
        );
        return;
      }

      const stockNum = Number(item.itemmaster_closingStock || 0);
      if (qtyNum > stockNum) {
        Swal.fire(
          "Error",
          `Insufficient stock for item: ${item.itemmaster_name}. Available: ${stockNum}, Requested: ${qtyNum}`,
          "error"
        );
        return;
      }
    }

    const payload = {
      branch: branch,
      user: userId,
      sfNo: formData.newPurNo,
      indentNo: selectedIndent,
      shift: formData.shift,
      remarks: formData.remarks,
      dept: formData.dept,
      items: activeRows.map((item) => ({
        itemCode: item.itemmaster_code,
        itemName: item.itemmaster_name,
        uom: item.itemmaster_uom,
        price: item.itemmaster_price,
        partNo: item.itemmaster_drawingNumber,
        qty: Number(item.qty || 0),
        vcode: item.vcode || "",
        ref: item.ref || "",
        purpose: item.purpose || "",
        spec: item.spec || "",
        vname: item.vnumber || "",
      })),
    };

    try {
      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await axios.post("https://rfs2.infraconsoft.net.in/api/sf_insert", payload);

      Swal.fire("Success", "Store Issue added successfully", "success");
      setIndent([]);
      navigate("/inventory/sfIssuesView");
    } catch (error) {
      console.error("Error submitting form:", error);
      Swal.fire("Error", "Submission failed. Please try again.", "error");
    }
  };

  // ---- access gate
  if (!canEntry) {
    return (
      <div id="admin-container">
        <InventoryNav />
        <div className="content-right" style={{ padding: 20 }}>
          <Access />
        </div>
      </div>
    );
  }

  return (
    <div id="admin-container">
      <InventoryNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">STORE ISSUE NOTE - Add</h2>
            <Link to="/inventory/sfIssuesView">
              <p className="back-button">
                <i className="fa-solid fa-backward"></i>Back
              </p>
            </Link>
          </div>
          <Time />
        </section>

        <div className="form-container">
          <form className="vehicle-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Store Issue No :</label>
                <input
                  type="text"
                  name="newPurNo"
                  value={formData.newPurNo}
                  onChange={handleChange}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label>Date :</label>
                <input
                  type="date"
                  name="endhrs"
                  value={new Date().toISOString().split("T")[0]}
                  readOnly
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Shift :</label>
                <select name="shift" value={formData.shift} onChange={handleChange}>
                  <option value="">Select</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                </select>
              </div>

              <div className="form-group">
                <label>Select Department :</label>
                <select name="dept" value={formData.dept} onChange={handleChange}>
                  <option value="">-- Select Dept --</option>
                  {dept.map((item, index) => (
                    <option key={index} value={item.departmentmaster_code}>
                      {item.departmentmaster_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Remarks :</label>
                <input
                  type="text"
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Indent Ref No :</label>
                <input
                  type="text"
                  name="indentNo_display"
                  value={selectedIndent}
                  readOnly
                  placeholder="Choose from 'Add Item'"
                />
              </div>
            </div>

            <div className="flex_buttons">
              <button
                type="button"
                className="button button-add"
                onClick={() => setShowModal(true)}
              >
                Add Item
              </button>
            </div>

            <div className="content-table">
              <table>
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Item Code</th>
                    <th>Item Name</th>
                    <th>Part No</th>
                    <th>UOM</th>
                    <th>Stock</th>
                    <th>Issued Qty</th>
                    <th>Price</th>
                    <th>Reference</th>
                     {canVehicle && (
                      <>
                    <th>Vehicle Code</th>
                    <th>Vehicle Number</th>
                    </>
                     )}
                    <th>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {indent.length > 0 ? (
                    indent.map((item, index) => (
                      <tr key={`${item.itemmaster_code}-${index}`}>
                        <td>{index + 1}</td>
                        <td>{item.itemmaster_code}</td>
                        <td>{item.itemmaster_name}</td>
                        <td>{item.itemmaster_drawingNumber}</td>
                        <td>{item.itemmaster_uom}</td>
                        <td>{item.itemmaster_closingStock}</td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            value={item.qty ?? ""}
                            onChange={(e) => handleRowChange(index, "qty", e.target.value)}
                          />
                        </td>
                        <td>{item.itemmaster_price}</td>
                        <td>
                          <input
                            type="text"
                            value={item.ref ?? ""}
                            onChange={(e) => handleRowChange(index, "ref", e.target.value)}
                          />
                        </td>
                         {canVehicle && (
                      <>
                        <td>
                          <input
                            type="text"
                            value={item.vcode ?? ""}
                            readOnly
                            onClick={() => openVehiclePicker(index)}
                            placeholder="Select Vehicle"
                            title="Click to pick vehicle"
                            style={{ cursor: "pointer" }}
                          />
                        </td>
                        <td>
                          <input type="text" value={item.vnumber ?? ""} readOnly />
                        </td>
                        </>
                         )}
                        
                        <td className="del">
                          <i
                            className="fa-solid fa-trash-can"
                            title="Remove this row (temporary)"
                            onClick={() => handleDeleteRow(index)}
                            style={{ cursor: "pointer" }}
                          ></i>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="13" style={{ textAlign: "center" }}>
                        No items added
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="btn-cls">
              <button type="submit" className="submit-btn">
                Checkout
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modals */}
      <IndentModal
        showModal={showModal}
        closeModal={() => setShowModal(false)}
        branch={branch}
        onSelectItem={handleSelectIndent}
      />

      <VehicleModal
        showModal={showVehicleModal}
        closeModal={() => setShowVehicleModal(false)}
        onSelectVehicle={handleVehicleSelect}
        branch={branch}
        rowIndex={currentVehicleIndex}
      />
    </div>
  );
};

export default AddSfIssues;
